import { useState, useEffect } from 'react';
import { Modal, Button, Table, Tag, notification, Alert, Space, Typography } from 'antd';
import { UploadOutlined, DownloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { t } from '../../../i18n/i18n';
import useImportCsv from '../../../hooks/useImportCsv';
import TreeMultiSelect from '../../TreeMultiSelect/TreeMultiSelect';
import './ImportAdvancedConfigModal.scss';

const { Text, Title } = Typography;

const ImportAdvancedConfigModal = ({
  open,
  onClose,
  onImport,
  testDifficulty = [],
  availableThemes = [],
  existingPresets = [],
  availableSubskills = [],
}) => {
  const [loading, setLoading] = useState(false);
  const [importedData, setImportedData] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [errors, setErrors] = useState([]);
  const [showSubskillsSelector, setShowSubskillsSelector] = useState(false);
  const [editingConfigIndex, setEditingConfigIndex] = useState(null);

  // CSV columns expected: Configuration Name, Difficulty, Theme, Quantity, Subskills, IsDefault
  const { importRef, readFile, result } = useImportCsv(
    ['configurationName', 'difficulty', 'theme', 'quantity', 'subskills', 'isDefault'],
    setLoading
  );
  // No longer need to fetch subskills as they are passed as a prop

  const processImportedData = (data) => {
    // Skip header row
    const dataWithoutHeader = data.slice(1);
    const groupedByConfig = {};
    const validationErrors = [];

    dataWithoutHeader.forEach((row, index) => {
      const rowNum = index + 2; // +2 because we skip header and index starts at 0
      // Validate row data
      if (!row.configurationName || row.configurationName.trim() === '') {
        validationErrors.push(t('IMPORT_ERROR_MISSING_CONFIG_NAME').replace('{{row}}', rowNum));
        return;
      }

      if (!row.difficulty || row.difficulty.trim() === '') {
        validationErrors.push(t('IMPORT_ERROR_MISSING_DIFFICULTY').replace('{{row}}', rowNum));
        return;
      }

      const quantity = Number(row.quantity);
      if (Number.isNaN(quantity) || quantity <= 0) {
        validationErrors.push(t('IMPORT_ERROR_INVALID_QUANTITY').replace('{{row}}', rowNum));
        return;
      }

      // Find difficulty by name (case insensitive)
      const difficulty = testDifficulty.find(
        (d) => d.name.toLowerCase() === row.difficulty.trim().toLowerCase()
      );
      if (!difficulty) {
        validationErrors.push(
          t('IMPORT_ERROR_INVALID_DIFFICULTY')
            .replace('DIFFICULTY', row.difficulty)
            .replace('{{row}}', rowNum)
        );
        return;
      }

      // Validate theme if provided
      let theme = null;
      if (row.theme && row.theme.trim() !== '' && availableThemes?.length > 0) {
        // Handle both string array and object array formats
        theme = availableThemes.find((t) => {
          const themeName = typeof t === 'string' ? t : t.name;
          return themeName.toLowerCase() === row.theme.trim().toLowerCase();
        });

        if (!theme) {
          validationErrors.push(
            t('IMPORT_ERROR_INVALID_THEME')
              .replace('{{row}}', rowNum)
              .replace('{{theme}}', row.theme)
          );
          return;
        }
      }

      // Parse subskills if provided
      const subSkillIds = [];
      if (row.subskills && row.subskills.trim() !== '') {
        // Split by semicolon and trim each subskill name
        const subskillNames = row.subskills
          .split(';')
          .map((s) => s.trim())
          .filter((s) => s);

        // Find matching subskills by name
        subskillNames.forEach((subskillName) => {
          const matchingSubskill = availableSubskills.find(
            (s) =>
              s.name?.toLowerCase() === subskillName.toLowerCase() ||
              s.languages?.es?.toLowerCase() === subskillName.toLowerCase() ||
              s.languages?.en?.toLowerCase() === subskillName.toLowerCase() ||
              s.languages?.pt?.toLowerCase() === subskillName.toLowerCase()
          );
          if (matchingSubskill) {
            subSkillIds.push(matchingSubskill.id);
          } else {
            validationErrors.push(
              t('IMPORT_ERROR_INVALID_SUBSKILL')
                .replace('{{row}}', rowNum)
                .replace('{{subskill}}', subskillName)
            );
          }
        });
      }

      // Check if isDefault is set (handle various true values)
      const isDefault =
        row.isDefault &&
        (String(row.isDefault)?.toLowerCase() === 'true' ||
          String(row.isDefault)?.toLowerCase() === 'yes' ||
          row?.isDefault === '1');

      // Group by configuration name
      const configurationName = row.configurationName.trim();
      if (!groupedByConfig[configurationName]) {
        groupedByConfig[configurationName] = {
          displayName: configurationName,
          difficultyConfig: [],
          isImported: true,
          isDefault: isDefault || false,
        };
      }

      groupedByConfig[configurationName].difficultyConfig.push({
        difficultyId: difficulty.id,
        difficultyName: difficulty.name,
        theme: theme ? (typeof theme === 'string' ? theme : theme.id) : '',
        themeName: theme ? (typeof theme === 'string' ? theme : theme.name) : '',
        quantity,
        order: groupedByConfig[configurationName].difficultyConfig.length,
        duration: 2, // Default duration per question
        disabled: false,
        listeningTest: false,
        displayName: `${t(`CREATE_TEST_GROUP_QUESTION_${difficulty.name}`)}${
          theme ? `/${typeof theme === 'string' ? theme : theme?.name}` : ''
        }`,
        maxQuantity: quantity,
        subSkillIds,
      });
    });

    // Check for duplicate configuration names with existing presets
    Object.keys(groupedByConfig).forEach((configurationName) => {
      if (existingPresets.some((preset) => preset.displayName === configurationName)) {
        validationErrors.push(
          t('IMPORT_ERROR_DUPLICATE_CONFIG_NAME').replace('{{name}}', configurationName)
        );
      }
    });
    // Filter out empty strings and null/undefined values
    const filteredErrors = validationErrors.filter((error) => error && error.trim() !== '');
    setErrors(filteredErrors);

    if (filteredErrors.length === 0) {
      const configurations = Object.values(groupedByConfig);

      // Ensure only one configuration is marked as default
      const defaultConfigs = configurations.filter((c) => c.isDefault);
      if (defaultConfigs.length > 1) {
        // Keep only the first one as default
        configurations.forEach((config, index) => {
          config.isDefault = index === configurations.findIndex((c) => c.isDefault);
        });
      }

      setImportedData(configurations);
      setPreviewData(configurations.slice(0, 3)); // Preview first 3 configurations
    } else {
      setImportedData([]);
      setPreviewData([]);
    }

    setLoading(false);
  };

  useEffect(() => {
    if (result && result.length > 0) {
      processImportedData(result);
    }
  }, [result]);

  const handleFileSelect = () => {
    // Reset state when selecting new file
    setImportedData([]);
    setPreviewData([]);
    setErrors([]);
    importRef.current.click();
  };

  const handleImport = () => {
    if (importedData.length === 0) {
      notification.error({
        message: t('IMPORT_NO_VALID_DATA'),
      });
      return;
    }

    // Pass the imported configurations to parent
    onImport(importedData);

    notification.success({
      message: t('IMPORT_SUCCESS_CONFIGS').replace('{{count}}', importedData.length),
    });

    handleClose();
  };

  const handleSubskillsChange = (configIndex, selectedSubSkillIds) => {
    const updatedData = [...importedData];
    // Update subskills for all difficultyConfig items in this preset
    updatedData[configIndex].difficultyConfig.forEach((config) => {
      config.subSkillIds = selectedSubSkillIds;
    });
    setImportedData(updatedData);

    // Update preview if it's in the preview range
    if (configIndex < 3) {
      setPreviewData(updatedData.slice(0, 3));
    }
  };

  const handleClose = () => {
    setImportedData([]);
    setPreviewData([]);
    setErrors([]);
    onClose();
  };

  const downloadTemplate = () => {
    const csvContent = `Configuration Name,Difficulty,Theme,Quantity,Subskills,IsDefault
Standard Config,Easy,,10,,true
Standard Config,Intermediate,,15,,
Standard Config,Hard,,5,,
Advanced Config,Easy,Grammar,8,,
Advanced Config,Easy,Vocabulary,7,,
Advanced Config,Intermediate,Grammar,10,,
Advanced Config,Intermediate,Vocabulary,10,,
Advanced Config,Hard,Grammar,5,,`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', 'advanced_configs_template.csv');
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const previewColumns = [
    {
      title: t('IMPORT_CONFIG_NAME'),
      dataIndex: 'displayName',
      key: 'displayName',
      render: (text) => <Text strong>{text}</Text>,
      width: '25%',
    },
    {
      title: t('IMPORT_DIFFICULTIES'),
      dataIndex: 'difficultyConfig',
      key: 'difficulties',
      render: (configs) => (
        <Space direction="vertical" size="small">
          {configs.map((config, index) => (
            <div key={index}>
              <Tag color="blue">{config.difficultyName}</Tag>
              {config.themeName && <Tag color="green">{config.themeName}</Tag>}
              <Tag>
                {config.quantity} {t('QUESTIONS').toLowerCase()}
              </Tag>
            </div>
          ))}
        </Space>
      ),
      width: '35%',
    },
    {
      title: t('IMPORT_SUBSKILLS'),
      dataIndex: 'difficultyConfig',
      key: 'subskills',
      render: (difficultyConfig, record) => {
        const configIndex = importedData.findIndex((c) => c.displayName === record.displayName);
        const subSkillIds = difficultyConfig?.[0]?.subSkillIds || [];
        const selectedSubskills = availableSubskills.filter((s) => subSkillIds?.includes(s.id));

        return (
          <div>
            <div className="mb-2">
              {selectedSubskills.length > 0 ? (
                selectedSubskills.slice(0, 3).map((subskill) => (
                  <Tag key={subskill.id} color="purple" className="mb-1">
                    {subskill.name}
                  </Tag>
                ))
              ) : (
                <Text type="secondary">{t('NO_SUBSKILLS_SELECTED')}</Text>
              )}
              {selectedSubskills.length > 3 && (
                <Text type="secondary">
                  {' '}
                  +{selectedSubskills.length - 3} {t('MORE')}
                </Text>
              )}
            </div>
            <Button
              size="small"
              onClick={() => {
                setEditingConfigIndex(configIndex);
                setShowSubskillsSelector(true);
              }}
            >
              {t('EDIT_SUBSKILLS')}
            </Button>
          </div>
        );
      },
      width: '25%',
    },
    {
      title: t('IMPORT_TOTAL_QUESTIONS'),
      dataIndex: 'difficultyConfig',
      key: 'total',
      render: (configs) => {
        const total = configs.reduce((sum, config) => sum + config.quantity, 0);
        return <Text strong>{total}</Text>;
      },
      width: '15%',
    },
  ];

  return (
    <Modal
      title={t('IMPORT_ADVANCED_CONFIGS_TITLE')}
      open={open}
      onCancel={handleClose}
      width="90%"
      style={{ maxWidth: '900px' }}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          {t('CANCEL')}
        </Button>,
        <Button
          key="import"
          type="primary"
          onClick={handleImport}
          disabled={importedData.length === 0 || errors.length > 0}
          loading={loading}
        >
          {t('IMPORT')}
        </Button>,
      ]}
    >
      <div className="import-advanced-config-modal">
        <Alert
          message={t('IMPORT_INSTRUCTIONS_TITLE')}
          description={
            <div>
              <p>{t('IMPORT_INSTRUCTIONS_DESCRIPTION')}</p>
              <ul>
                <li>{t('IMPORT_INSTRUCTION_1')}</li>
                <li>{t('IMPORT_INSTRUCTION_2')}</li>
                <li>{t('IMPORT_INSTRUCTION_3')}</li>
                <li>{t('IMPORT_INSTRUCTION_4')}</li>
                <li>{t('IMPORT_INSTRUCTION_5')}</li>
              </ul>
            </div>
          }
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          className="mb-3"
        />

        <Space className="mb-3">
          <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>
            {t('DOWNLOAD_TEMPLATE')}
          </Button>

          <Button icon={<UploadOutlined />} onClick={handleFileSelect} type="primary">
            {t('SELECT_CSV_FILE')}
          </Button>
        </Space>

        <input
          ref={importRef}
          type="file"
          accept=".csv,.xls,.xlsx"
          onChange={readFile}
          style={{ display: 'none' }}
        />

        {errors.length > 0 && (
          <Alert
            message={t('IMPORT_ERRORS_FOUND')}
            description={
              <ul className="error-list">
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            }
            type="error"
            showIcon
            className="mb-3"
          />
        )}

        {previewData.length > 0 && (
          <div className="preview-section">
            <Title level={5}>{t('IMPORT_PREVIEW')}</Title>
            <Table
              dataSource={previewData}
              columns={previewColumns}
              rowKey={(record, index) => index}
              pagination={false}
              size="small"
            />
            {importedData.length > 3 && (
              <Text type="secondary" className="mt-2">
                {t('IMPORT_AND_MORE').replace('{{count}}', importedData.length - 3)}
              </Text>
            )}
          </div>
        )}
      </div>

      {/* Subskills Selection Modal */}
      <Modal
        title={t('SELECT_SUBSKILLS_FOR_CONFIG')}
        open={showSubskillsSelector}
        onCancel={() => {
          setShowSubskillsSelector(false);
          setEditingConfigIndex(null);
        }}
        onOk={() => {
          setShowSubskillsSelector(false);
          setEditingConfigIndex(null);
        }}
        width={600}
      >
        {editingConfigIndex !== null && (
          <div>
            <p className="mb-3">
              <Text strong>{t('CONFIGURATION')}:</Text>{' '}
              {importedData[editingConfigIndex]?.displayName}
            </p>
            <TreeMultiSelect
              options={availableSubskills}
              value={
                importedData[editingConfigIndex]?.difficultyConfig?.[0]?.subSkillIds || []
              }
              onChange={(selectedIds) => handleSubskillsChange(editingConfigIndex, selectedIds)}
              label={t('SELECT_SUBSKILLS')}
              labelName="name"
              width="100%"
              dropdownWidth={500}
              showSelectUnselectAll
            />
          </div>
        )}
      </Modal>
    </Modal>
  );
};

export default ImportAdvancedConfigModal;
